import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchItemsAsync, selectItems, selectLoading, selectError } from './inventorySlice';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import AddItemModal from './AddItemModal';
import ServerStatusIndicator from '../../components/ServerStatusIndicator';

const ItemList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const items = useSelector(selectItems);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchItemsAsync() as any);
    }, [dispatch]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <ErrorMessage message={error} onRetry={() => dispatch(fetchItemsAsync() as any)} />
            </div>
        );
    }

    // Flatten items dengan suppliers - setiap kombinasi jadi row terpisah
    const flattenedItems = items.flatMap(item => {
        if (item.suppliers && item.suppliers.length > 0) {
            return item.suppliers.map(supplier => ({
                ...item,
                currentSupplier: supplier
            }));
        }
        // Items tanpa supplier tetap ditampilkan
        return [{ ...item, currentSupplier: null }];
    });

    return (
        <div className="p-6">
            <AddItemModal 
                isOpen={isAddModalOpen} 
                onClose={() => setIsAddModalOpen(false)} 
            />
            
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Inventory Management</h1>
                    <div className="mt-2">
                        <ServerStatusIndicator showLabel={true} />
                    </div>
                </div>
                <button 
                    onClick={() => navigate('/inventory/add')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg font-semibold flex items-center gap-2 transition-all hover:shadow-xl"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Item
                </button>
            </div>
            
            {flattenedItems.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-lg">No items found</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {/* Optimized table - no horizontal scroll */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 inventory-table">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tight w-[10%]">SKU</th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tight w-[30%]">Item Name</th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tight w-[12%]">Brand</th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tight w-[12%]">Supplier</th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-tight w-[10%]">Cost</th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-tight w-[10%]">Sell</th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-tight w-[10%]">Category</th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-tight w-[6%]">
                                        <span className="hidden lg:inline">Actions</span>
                                        <span className="lg:hidden">Act</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {flattenedItems.map((item, idx) => {
                                    const supplier = item.currentSupplier;
                                    return (
                                        <tr key={`${item.id}-${idx}`} className={`hover:bg-gray-50 ${supplier?.is_preferred ? 'bg-green-50' : ''}`}>
                                            {/* SKU - compact with tooltip */}
                                            <td className="px-3 py-2 text-xs font-medium text-gray-900 truncate" title={item.sku}>
                                                <span className="block max-w-full overflow-hidden text-ellipsis">
                                                    {item.sku}
                                                </span>
                                                {item.barcode && (
                                                    <span className="block text-[10px] text-gray-400 font-mono mt-0.5 truncate" title={item.barcode}>
                                                        {item.barcode}
                                                    </span>
                                                )}
                                            </td>
                                            
                                            {/* Item Name - most important, gets more space */}
                                            <td className="px-3 py-2 text-sm text-gray-900">
                                                <div className="font-medium break-words" style={{ maxWidth: '280px', wordBreak: 'break-word' }} title={item.name}>
                                                    {item.name}
                                                </div>
                                                {/* Show warranty + status as compact badges */}
                                                <div className="flex gap-1 mt-1">
                                                    {item.has_warranty && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                                                            W
                                                        </span>
                                                    )}
                                                    {!item.is_active && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                                                            Inactive
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            
                                            {/* Brand - compact */}
                                            <td className="px-3 py-2 text-xs">
                                                {supplier?.brand ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 truncate max-w-full" title={supplier.brand}>
                                                        {supplier.brand}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 text-[10px]">-</span>
                                                )}
                                            </td>
                                            
                                            {/* Supplier - compact with star */}
                                            <td className="px-3 py-2 text-xs">
                                                {supplier ? (
                                                    <div className="flex items-center gap-1">
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium truncate max-w-full ${
                                                            supplier.is_preferred 
                                                                ? 'bg-green-100 text-green-800' 
                                                                : 'bg-blue-100 text-blue-700'
                                                        }`} title={supplier.supplier_name}>
                                                            {supplier.is_preferred && '⭐ '}
                                                            {supplier.supplier_name}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-[10px]">-</span>
                                                )}
                                            </td>
                                            
                                            {/* Cost Price - right aligned, compact */}
                                            <td className="px-3 py-2 text-xs text-right font-semibold">
                                                {supplier ? (
                                                    <span className="text-gray-900" title={`Rp ${Number(supplier.cost_price).toLocaleString('id-ID')}`}>
                                                        {Number(supplier.cost_price) >= 1000000 
                                                            ? `${(Number(supplier.cost_price) / 1000).toFixed(0)}k`
                                                            : Number(supplier.cost_price).toLocaleString('id-ID', { maximumFractionDigits: 0 })
                                                        }
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500" title={`Rp ${Number(item.cost_price).toLocaleString('id-ID')}`}>
                                                        {Number(item.cost_price) >= 1000000 
                                                            ? `${(Number(item.cost_price) / 1000).toFixed(0)}k`
                                                            : Number(item.cost_price).toLocaleString('id-ID', { maximumFractionDigits: 0 })
                                                        }
                                                    </span>
                                                )}
                                            </td>
                                            
                                            {/* Sell Price - right aligned, compact */}
                                            <td className="px-3 py-2 text-xs text-right font-semibold text-blue-700" title={`Rp ${Number(item.sell_price).toLocaleString('id-ID')}`}>
                                                {Number(item.sell_price) >= 1000000 
                                                    ? `${(Number(item.sell_price) / 1000).toFixed(0)}k`
                                                    : Number(item.sell_price).toLocaleString('id-ID', { maximumFractionDigits: 0 })
                                                }
                                            </td>
                                            
                                            {/* Category - compact badge */}
                                            <td className="px-3 py-2 text-center">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 truncate max-w-full" title={item.category}>
                                                    {item.category}
                                                </span>
                                            </td>
                                            
                                            {/* Actions - icon only */}
                                            <td className="px-3 py-2 text-center">
                                                <button
                                                    onClick={() => navigate(`/inventory/edit/${item.id}`)}
                                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors inline-flex items-center justify-center"
                                                    title="Edit Item"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                        <p className="text-sm text-gray-700">
                            Total Rows: <span className="font-medium">{flattenedItems.length}</span>
                            <span className="text-gray-500 ml-3">
                                ({items.length} unique items)
                            </span>
                        </p>
                        <p className="text-xs text-gray-500">
                            💡 Green rows = preferred suppliers | W = Warranty
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItemList;